import { ComponentFactoryResolver, Directive, Input, Renderer2, TemplateRef, ViewContainerRef } from '@angular/core';
import { ThemePalette } from '@angular/material/core';
import { MatProgressSpinner, MatSpinner } from '@angular/material/progress-spinner';

@Directive({ selector: '[spinOn]' })
export class SpinOnDirective {
    private static MAX_DIAMETER = 100;
    private static DEFAULT_DIAMETER = 18;
    private color: ThemePalette = 'primary';
    private diameter: number;
    private isSpinning = null;
    private spinner: MatProgressSpinner = null;
    private wrapper: any;

    constructor(
        private componentFactoryResolver: ComponentFactoryResolver,
        private templateRef: TemplateRef<any>,
        private viewContainer: ViewContainerRef,
        private renderer: Renderer2,
    ) {
    }

    @Input() set spinOn(condition: boolean) {
        if (!condition) {
            this.removeWrapper();
            this.viewContainer.clear();
            this.viewContainer.createEmbeddedView(this.templateRef);
            return;
        }

        if (!!condition !== this.isSpinning) {
            let clientWidth = 0;
            let clientHeight = 0;

            // Capture the dimensions if the original content exists
            if (this.viewContainer.length > 0) {
                const originalElement = (this.viewContainer.get(0) as any).rootNodes[0];
                if (originalElement) {
                    clientWidth = originalElement.clientWidth;
                    clientHeight = originalElement.clientHeight;
                }
            }

            this.spinner = null;
            this.viewContainer.clear();
            this.isSpinning = condition;
            if (!condition) {
                this.viewContainer.createEmbeddedView(this.templateRef);
            } else if (condition) {
                this.addSpinner(clientWidth, clientHeight);
            }
        }
    }

    @Input()
    set spinOnColor(color: ThemePalette) {
        this.color = color;
        if (this.spinner) {
            this.spinner.color = color;
        }
    }

    @Input()
    set spinOnDiameter(diameter: number) {
        this.diameter = diameter;
        if (this.spinner) {
            this.spinner.diameter = diameter;
        }
    }

    private setStyles(element: any, styles: { [key: string]: any }) {
        Object.keys(styles).forEach((styleName) => {
            this.renderer.setStyle(element, styleName, styles[styleName]);
        });
    }

    private calculateOptimalDiameter(width: number, height: number): number {
        const lesserDimension = Math.min(width, height);
        const calculatedDiameter = Math.floor(lesserDimension * 0.75);
        return Math.min(calculatedDiameter, SpinOnDirective.MAX_DIAMETER);
    }

    private addSpinner(width: number, height: number) {
        // set min width and height
        const minSize = 24;

        if (width < minSize) {
            width = minSize;
        }

        // set min height
        if (height < minSize) {
            height = minSize;
        }

        // Create wrapper div
        this.wrapper = this.renderer.createElement('div');
        this.setStyles(this.wrapper, {
            width: `${ width }px`,
            height: `${ height }px`,
            display: 'flex',
            'flex-direction': 'column',
            'align-items': 'center',
            'justify-content': 'center',
        });

        // Create spinner component
        const componentFactory = this.componentFactoryResolver.resolveComponentFactory(MatSpinner);
        const componentRef = this.viewContainer.createComponent<MatProgressSpinner>(componentFactory);

        // Set spinner properties
        const spinnerInstance = componentRef.instance;
        spinnerInstance.diameter = this.diameter || this.calculateOptimalDiameter(width, height) || SpinOnDirective.DEFAULT_DIAMETER;
        spinnerInstance.color = this.color;
        spinnerInstance.mode = 'indeterminate';

        // Append spinner to wrapper
        this.renderer.appendChild(this.wrapper, componentRef.location.nativeElement);

        // Append wrapper to parent
        const parent = this.viewContainer.element.nativeElement.parentElement;
        this.renderer.appendChild(parent, this.wrapper);

        this.spinner = componentRef.instance;
    }

    private removeWrapper() {
        if (this.wrapper) {
            this.renderer.removeChild(this.wrapper.parentNode, this.wrapper);
            this.wrapper = null;
            this.isSpinning = null;
        }
    }
}
